/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Chip, Dialog, DialogTitle, DialogContent, Typography, IconButton, Box, TextField } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import React, { ChangeEvent, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Application, ApplicationReducer, ApplicationShare, ApplicationState, UserPermission } from "../../../data/interfaces";
import { useStyles } from "./style";
import GreyButton from '../../../elements/GreyButton';
import PrimaryButton from '../../../elements/PrimaryButton';
import Joi from "joi";
import { connect } from "react-redux";
import { shareApplication } from "../../../data/operations";
import InfoBanner from "../../shared/InfoBanner";
import { createBrowserHistory } from 'history';

interface ShareAppProps {
    open: boolean;
    onClose: () => void;
    application?: Application;
    applicationReducer: ApplicationReducer,
    dispatchShareApplication: (application: Application, applicationShareData: ApplicationShare) => Promise<any>,
}

function ShareApplication(props: ShareAppProps) {

    const browserHistory = createBrowserHistory();
    const classes = useStyles();
    const intl = useIntl();
    const {
        open, onClose, dispatchShareApplication,
        application, applicationReducer
    } = props;
    const [userEmail, setUserEmail] = React.useState<string>("");
    const [sharedUserEmails, setSharedUserEmails] = React.useState<string[]>([]);
    const [invalidEmail, setEmailInvalid] = React.useState<boolean>(false);
    const [shareInProgress, setShareInprogress] = React.useState<boolean>(false);
    const [removeInProgress, setRemoveInprogress] = React.useState<boolean>(false);

    const onUserEmailInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEmailInvalid(false);
        setUserEmail(event.target.value);
    };

    useEffect(() => {   
        if (application && application.sharedWith) {
            setSharedUserEmails(application.sharedWith.map((userPermission) => userPermission.userEmail));
        }
    }, [application]);

    const isValidEmail = (email: string): boolean => {
        const emailValidation = Joi.string().email({ tlds: { allow: false }}).validate(email);
        if (emailValidation.error) {
            setEmailInvalid(true);
            return false;
        } else {
            setEmailInvalid(false);
            return true;
        }
    }

    const removeUserEmailFromList = (email: string) => {
        sharedUserEmails.splice(sharedUserEmails.indexOf(email), 1);
        setSharedUserEmails([...sharedUserEmails]);
        setRemoveInprogress(true);
    };

    const shareApplication = async() => {
        const userPermissions: UserPermission[] = [];
        sharedUserEmails.forEach((email) => {
            userPermissions.push({
                userEmail: email
            });
        });
        if (application) {
            setShareInprogress(true);
            await dispatchShareApplication(application, {
                applicationId: application.applicationId!, 
                userPermissions: userPermissions
            }).then((res) => {
                if (res !== null) {
                    browserHistory.push('applications');
                    onClose();
                }
            }).finally(() => {
                setShareInprogress(false);
            });
        }
    };

    const shareButtonLabel = () => {
        return intl.formatMessage({
            id: 'application.share.confirm',
            defaultMessage: 'Confirm'
        });
    }
    
    const cancelButtonLabel = () => {
        return intl.formatMessage({
            id: 'application.share.share',
            defaultMessage: 'Cancel'
        });
    }

    const emailInputPlaceholder = intl.formatMessage({
            id: 'application.share.email.placeholder',
            defaultMessage: 'Type an email address and press Enter'
    });

    const InvalidEmailError = () => {
        return(
            <Box className={classes.invalidEMail} data-testid="invalid-email">
                <FormattedMessage 
                    id="application.share.userEmail.invalidEmail" 
                    defaultMessage="Invalid email address" />
            </Box>
        )
    }

    const applicationShareFormContent = () => {
        return (
            <Box>
                <Box
                    justifyContent="space-between"
                    alignItems="flex-start"
                    display="flex"
                    flexDirection="column"
                    my={1}
                    width={1}
                >
                    <Typography className={classes.textLabelWithAsterisk}>
                        <FormattedMessage id='application.share.userEmail' defaultMessage='Email Addresses' />
                        <sup className={classes.asterisk}> * </sup>
                    </Typography>
                    <TextField
                        onChange={onUserEmailInputChange}
                        onKeyPress={(event) => {
                            if (event.key === 'Enter') {
                                if(isValidEmail(userEmail)){
                                    setSharedUserEmails([...sharedUserEmails, userEmail]);
                                    setUserEmail("");
                                }
                            }
                        }}
                        value={userEmail}
                        className={classes.textInput}
                        placeholder={emailInputPlaceholder}
                        variant="outlined"
                        data-testid="app-share-user-email"
                    />
                    { invalidEmail && <InvalidEmailError />}
                    
                </Box>
                <Box display="flex" gridGap={5} my={1} flexWrap="wrap" data-testid="emails-wrapper">
                    {
                        sharedUserEmails.map((email, index) => {
                            return (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => {
                                        removeUserEmailFromList(email);
                                    }}
                                    className={classes.emailChip}
                                    data-testid="app-share-email-address"
                                />
                            )
                        })
                    }
                </Box>
                <Box className={classes.messageBox}>
                    <InfoBanner
                        message={
                            <Box>
                                <Typography variant='subtitle2' className={classes.infoBannerText}>
                                    <FormattedMessage 
                                        id='application.share.message' 
                                        defaultMessage='The application "{name}" will be shared with the users in read-only mode.'
                                        values={{ name: <strong data-testid="shared-app-name">{application?.name}</strong>}}  />
                                </Typography>
                            </Box>
                        }
                        testId='app-share-info-banner'
                    />
                </Box>
                <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                    <GreyButton
                        dataTestId="share-cancel-button"
                        onClick={() => onClose()}
                        className={classes.cancelBtn}
                        buttonLabel={cancelButtonLabel()}
                    />
                    <PrimaryButton
                        dataTestId="share-button"
                        disabled={ (!removeInProgress && sharedUserEmails.length === 0 ) || shareInProgress}
                        onClick={() => shareApplication()}
                        buttonLabel={shareButtonLabel()}
                        className={classes.dialogBtn}
                        id="share-button"
                    />               
                </Box>
            </Box>
        );
    };
    
    return (
        <React.Fragment>
            <Dialog
                data-cyid="app-share-dialog"
                open={open}
                onClose={(_,reason) => {
                    if (reason !== 'backdropClick') {
                        onClose()
                    }
                }}
                fullWidth={true}
                classes={{ paper: classes.customWidth }}
            >
                <DialogTitle>
                    <Typography className={classes.primaryTitle}>
                        <FormattedMessage 
                            id="application.share.title" 
                            defaultMessage="Share Application" />
                    </Typography>
                    <IconButton className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent classes={{ root: classes.dialogContent }}>
                    {applicationShareFormContent()}
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer
    }
};

const mapDispatchToProps = {
    dispatchShareApplication: shareApplication
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareApplication);
